/* eslint-disable @typescript-eslint/no-explicit-any */
import Templater from '@hc-frontend/shared-assets/src/js/utils/dom/Templater'
import { numberToCurrency } from '@hc-frontend/utils'
import detailsDentalFees from 'core/controllerPlans/detailsDentalFees'
import getPremium from 'core/controllerPlans/getPremium'
import moneyFormat from 'utils/format/money'
import toggleClass from 'utils/DOM/toggleClass'
import innerHTML from 'utils/DOM/innerHTML'
import getById from 'utils/DOM/getById'
import 'bootstrap/js/src/tab'
import { countChildren } from '@hc-frontend/business'
import { getLatitudeFees } from 'core/controllerPlans/getLatitudeFees'
import {
  AMERITAS_DENTAL,
  AMERITAS_VISION,
  DENTAL,
  VISION_ID
} from 'core/constants/products'
import getBadgeDental from 'core/controllerPlans/getBadgeDental'
import { enableFlexBenefits } from 'utils/experiments/enableFlexBenefits'
import { parseBenefits, getExternalPlanTotal } from 'utils/plans/parseExternalBenefits'
import { IS_AGENT } from 'core/constants/environment'

const asMoneyWithoutDecimal = numberToCurrency
const asMoney = moneyFormat

interface RenderViewArgs {
  /** IDs of primary dental and vision add-ons */
  addOns: number[]

  /** Latitude plan IDs */
  latitude: number[]

  /** Supplemental plan IDs */
  supplemental: number[]

  /** An object with plan IDs as the keys and plans as the values */
  plans: any

  /** Whether the customer already has an active dental plan with Pivot */
  hasDentalEnabled: boolean

  /** Storage */
  hcStorage: any

  /** Selected duration */
  period: number
}

function renderView({
  addOns = [],
  latitude = [],
  supplemental = [],
  plans = {},
  hasDentalEnabled = false,
  hcStorage,
  period
}: RenderViewArgs) {
  const hasPrimaryAddOns = addOns.length > 0
  const helpers = {
    asMoney,
    asMoneyWithoutDecimal
  }

  if (hasPrimaryAddOns && !hasDentalEnabled) {
    const renaissanceDentalPlans = Object.values(plans).filter(
      (plan: any) => plan.productId === DENTAL
    )
    const renaissanceVisionPlan = Object.values(plans).find(
      (plan: any) => plan.planId === VISION_ID
    )
    renderRenaissance({
      dentalPlans: renaissanceDentalPlans,
      visionPlan: renaissanceVisionPlan,
      hcStorage,
      helpers
    })

    const ameritasDentalPlans = Object.values(plans).filter(
      (plan: any) => plan.productId === AMERITAS_DENTAL
    )
    renderAmeritasDental({ plans: ameritasDentalPlans, helpers })

    const ameritasVisionPlans = Object.values(plans).filter(
      (plan: any) => plan.productId === AMERITAS_VISION
    )
    renderAmeritasVision({ plans: ameritasVisionPlans, helpers })
  }

  const hasLatitude = latitude.length > 0 && !enableFlexBenefits()
  if (hasLatitude) {
    const templateDental = Templater(getById('template-latitude').innerText)
    innerHTML(
      '#pills-latitude-wrap',
      latitude
        .map(id =>
          templateDental(
            {
              ...plans[id],
              isPreferred: /preferred/i.test(plans[id].planName),
              isRecommended: /select/i.test(plans[id].planName)
            },
            { ...helpers, getLatitudeFees }
          )
        )
        .join('')
    )
  }

  const externalPlans = Array.isArray(hcStorage.externalPlans)
    ? hcStorage.externalPlans
    : []
  const hasExternalPlans = externalPlans.length > 0 && enableFlexBenefits()
  if (hasExternalPlans) {
    const templateFlexBenefits = Templater(getById('template-flex-benefits').innerText)
    innerHTML(
      '#pills-flex-benefits-wrap',
      externalPlans
        .map(externalPlan => {
          const selectedLevels =
            (hcStorage.selectedBenefitLevels || {})[externalPlan.id] || {}
          const totalAmount = getExternalPlanTotal(externalPlan, selectedLevels)
          return templateFlexBenefits(
            {
              planId: externalPlan.id,
              isRecommended: externalPlan.altName === 'FlexProtectPro',
              planName: externalPlan.name,
              totalAmount,
              IS_AGENT,
              benefits: parseBenefits(externalPlan.products, {
                suppressNoCoverageSelected: true,
                selectedLevels
              })
            },
            { ...helpers, getLatitudeFees }
          )
        })
        .join('')
    )
  }

  const hasSupplemental = supplemental.length > 0
  if (hasSupplemental) {
    const templateSupplemental = Templater(getById('template-supplemental').innerText)

    innerHTML(
      '#wrap-supplemental',
      supplemental
        .map(id =>
          templateSupplemental({
            id,
            benefit: asMoneyWithoutDecimal(+plans[id].coreBenefits.supplemental),
            price: asMoney(plans[id].durations[period][1].monthly.premium)
          })
        )
        .join('')
    )
  }

  const hasSecondaryAddOns = hasLatitude || hasExternalPlans
  const hasAnyAddOns = hasPrimaryAddOns || hasSecondaryAddOns
  const shouldShowProductsInfo = !hasDentalEnabled || hasSecondaryAddOns

  // show container
  toggleClass('.products', 'd-none', !hasAnyAddOns)
  toggleClass(
    '#pills-tab',
    'd-none',
    !(hasPrimaryAddOns && hasSecondaryAddOns) && !hasDentalEnabled
  )

  toggleClass('#pills-dental', 'd-flex', hasPrimaryAddOns && !hasDentalEnabled)
  toggleClass('#pills-latitude', 'd-flex', hasLatitude)
  toggleClass('#pills-flex-benefits', 'd-flex', hasExternalPlans)

  toggleClass('.products__banner', 'd-flex', hasDentalEnabled)
  toggleClass('.products__banner-message', 'd-flex', shouldShowProductsInfo)
  toggleClass('.products__header', 'd-flex', shouldShowProductsInfo)

  toggleClass(
    document.body,
    'no-dental',
    hasPrimaryAddOns !== hasSecondaryAddOns || hasDentalEnabled
  )

  toggleClass(document.body, 'dental-plans-odd', addOns.length % 2 === 0)
  // supplemental
  toggleClass('.js-extend', 'd-none', !hasSupplemental)

  // show disclaimer
  // apparently the first value in this array can be undefined, so we have to check it to prevent an exception
  let hasCAFee = false
  if (addOns.length > 0 && addOns[0] != null) {
    hasCAFee = plans[addOns[0]].durations[0].monthly.fees.some(
      fee => fee.name === 'communicatingForAmerica'
    )
  }
  toggleClass('#communicatingForAmericaFee-disclaimer', 'd-none', !hasCAFee)
  toggleClass('.disclaimer__dental', 'd-none', !hasPrimaryAddOns)
  toggleClass(
    '.disclaimer__latitude-large',
    'd-none',
    !hasLatitude || enableFlexBenefits()
  )
  toggleClass('.disclaimer__flex-benefits', 'd-none', !enableFlexBenefits())
}

function renderRenaissance({
  dentalPlans,
  visionPlan,
  hcStorage,
  helpers
}: {
  dentalPlans: any[]
  visionPlan: any
  hcStorage: any
  helpers: any
}): void {
  const dentalValuePlan = dentalPlans.find(plan => /value/i.test(plan.planName))
  const plans = dentalValuePlan
    ? [
        dentalValuePlan,
        ...dentalPlans.filter(plan => plan.planId !== dentalValuePlan.planId)
      ]
    : dentalPlans

  const visionPremium = getPremium(visionPlan)
  const childrenFound = countChildren(hcStorage.members) > 0
  const template = Templater(getById('template-dental-plan-renaissance').innerText)
  innerHTML(
    '#pills-dental-wrap',
    plans
      .map(plan =>
        template(
          {
            planName: plan.planName,
            planId: plan.planId,
            visionPremium: visionPlan
              ? asMoney(getPremium(plan) + visionPremium)
              : undefined,
            childrenFound,
            premium: asMoney(getPremium(plan)),
            detailsDental: detailsDentalFees(plan),
            detailsDentalPremium: detailsDentalFees(plan, visionPlan),
            isRecommended: dentalValuePlan != null,
            isEssential: /essential/i.test(plan.planName),
            isMaximum: /maximum/i.test(plan.planName)
          },
          helpers
        )
      )
      .join('')
  )
}

function renderAmeritasDental({ plans, helpers }: { plans: any[]; helpers: any }): void {
  const template = Templater(getById('template-dental-plan-ameritas').innerText)
  innerHTML(
    '#pills-ameritas-dental-wrap',
    plans
      .map(plan =>
        template(
          {
            planName: plan.planName,
            planId: plan.planId,
            premium: asMoney(getPremium(plan)),
            isRecommended: false,
            marketingHighlight: getBadgeDental(plan.planName, []).message
          },
          helpers
        )
      )
      .join('')
  )
}

function renderAmeritasVision({ plans, helpers }: { plans: any[]; helpers: any }): void {
  const template = Templater(getById('template-vision-plan-ameritas').innerText)
  innerHTML(
    '#pills-ameritas-vision-wrap',
    plans
      .map(plan =>
        template({
          planName: plan.planName,
          planId: plan.planId,
          premium: asMoney(getPremium(plan)),
          isRecommended: false,
          marketingHighlight: getBadgeDental(plan.planName, []).message
        })
      )
      .join('')
  )
}

export default renderView
